import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Filter, Mail, MessageSquareReply, RefreshCcw, Search as SearchIcon, Users } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { MarketplaceBrandMark } from '../ManageTickets/EventMarketplaceDeepLinks';
import mt from '../ManageTickets/ManageTickets.module.css';
import styles from './SupportTicketsPage.module.css';
import { globalContext } from '../../Provider/AppProvider';
import { GET_USER_SUPPORT_SCOPE, GET_USERS_BY_IDS, GET_VL_SUPPORT_TICKETS, GET_VL_SUPPORT_TICKET_USERS } from './api/supportTicketsQuery';
import {
  getVlSupportInboxStatus,
  getVlSupportInsights,
  type VlSupportInboxStatus,
  type VlSupportInsights,
} from '../ManageTickets/api/vlListing.api';
import { deriveOwnerInitials, VL_SUPPORT_OWNERS } from '../ManageTickets/vlSupportEmail';
import { VlSupportThread } from './components/VlSupportThread';
import { completeVlSupportTickets } from '../ManageTickets/api/vlListing.api';
import { ticketOwners } from './supportOwners';
import { subscribeVlSupportUpdates } from './hooks/useVlSupportAlerts';
import {
  SUPPORT_MARKETPLACES,
  SUPPORT_OWNER_PIE_ORDER,
  SUPPORT_TYPE_LABELS,
  SupportOwnerFilter,
  SupportTypeFilter,
  VlSupportTicketRow,
  VlSupportTicketType,
  formatDateTime,
  BARCODE_ACCOUNT_SLICES,
  barcodeGroupsFromTickets,
  barcodeSlices,
  formatDuration,
  inboxWarning,
  needsReply,
  isWaitingOnUs,
  completedVisibleSince,
  pieCounts,
  sortSupportTickets,
  syncAccountsSummary,
  SortDir,
  SupportSortKey,
  supportTicketsWhere,
  ticketOpenSeconds,
  ticketUsersFromListings,
  addLinkedUsers,
  isUnlinked,
  matchesSupportSearch,
  formatFullDateTime,
  keepOrderWhileOpen,
  type SupportTicketUser,
  userSupportScope,
} from './supportTicketUtils';

type Tab = 'open' | 'completed' | 'sold' | 'insights';
const PAGE_SIZE = 25;
const TYPE_OPTIONS: Array<{ value: SupportTypeFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'barcode_sync', label: SUPPORT_TYPE_LABELS.barcode_sync },
  { value: 'not_populating', label: 'Not Populating On A Marketplace' },
  { value: 'need_pid', label: SUPPORT_TYPE_LABELS.need_pid },
  { value: 'inbound', label: SUPPORT_TYPE_LABELS.inbound },
  { value: 'other', label: SUPPORT_TYPE_LABELS.other },
];
/**
 * Legend order for a two-column grid: each platform's [SSM] slice on the left with its
 * [PM] slice on the right, so Ticketmaster [SSM] and Ticketmaster [PM] share a row.
 */
const legendRows = <T extends { group: string }>(slices: T[]): Array<T | null> => {
  const platformOf = (group: string) => group.split(' · ')[1] || '';
  const ssm = slices.filter((x) => x.group.startsWith('Season Seat Manager'));
  const pm = slices.filter((x) => !x.group.startsWith('Season Seat Manager'));
  const used = new Set<T>();
  const rows: Array<T | null> = [];
  for (const left of ssm) {
    const right = pm.find((x) => !used.has(x) && x.group.startsWith('Purchase Manager ·') && platformOf(x.group) === platformOf(left.group));
    if (right) used.add(right);
    rows.push(left, right ?? null);
  }
  for (const right of pm) if (!used.has(right)) rows.push(null, right);
  return rows;
};

/** Insights cards: Need PID first, then barcodes, not populating, other. */
const TYPE_ORDER = ['need_pid', 'barcode_sync', 'not_populating', 'other', 'inbound'];
const RANGE_OPTIONS = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
  { value: 0, label: 'All time' },
];

const percent = (rate: number | null) => (rate == null ? '—' : `${(rate * 100).toFixed(1)}%`);
const OWNER_COLORS = ['#4f46e5', '#0ea5e9', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#0f766e', '#94a3b8'];
const LOGO_ID_BY_NAME: Record<string, string> = Object.fromEntries(SUPPORT_MARKETPLACES.map((m) => [m.name, m.logoId]));

/** Marketplace logos instead of names, so six marketplaces still fit on one line. */
const MarketplaceLogos = ({ names }: { names?: string[] | null }) =>
  names?.length ? (
    <div className={styles.logoRow}>
      {names.map((name) => (
        <span key={name} className={styles.logoChip} title={name} aria-label={name}>
          <MarketplaceBrandMark id={LOGO_ID_BY_NAME[name] ?? name.replace(/\s+/g, '').toLowerCase()} size={18} />
        </span>
      ))}
    </div>
  ) : null;

/** Event name with its date underneath. */
const EventCell = ({ name, date, unread }: { name: string; date?: string | null; unread?: number | null }) => (
  <>
    <span className={styles.eventName}>{name}</span>
    {Number(unread || 0) > 0 && <span className={styles.unread}>{unread} new</span>}
    {date && <div className={styles.eventMeta}>{date}</div>}
  </>
);

type SupportTicketsPageProps = {
  /** Show only this user's support tickets and insights (the Support Tickets tab on a user's profile). */
  userId?: string;
};

export default function SupportTicketsPage({ userId }: SupportTicketsPageProps = {}) {
  const embedded = Boolean(userId);
  const apolloClient = useApolloClient();
  const { globalState } = useContext<any>(globalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const myInitials = deriveOwnerInitials(globalState?.profile?.firstName, globalState?.profile?.lastName);

  const [tab, setTab] = useState<Tab>('open');
  const [typeFilter, setTypeFilter] = useState<SupportTypeFilter>('all');
  const [ownerFilter, setOwnerFilter] = useState<SupportOwnerFilter>('company');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<{ key: SupportSortKey; dir: SortDir }>({ key: 'default', dir: 'desc' });
  const [openDropdown, setOpenDropdown] = useState<'type' | 'owner' | null>(null);

  const [rows, setRows] = useState<VlSupportTicketRow[]>([]);
  const [counts, setCounts] = useState({ open: 0, completed: 0 });
  const [ticketUsers, setTicketUsers] = useState<Record<string, SupportTicketUser[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedTicketId = searchParams.get('ticket');

  const [inboxStatus, setInboxStatus] = useState<VlSupportInboxStatus | null>(null);

  // User profile: the support tickets raised on this user's listings, and which of them are sold.
  const [userScope, setUserScope] = useState<{ ticketIds: string[]; soldTicketIds: string[] } | null>(null);
  const loadUserScope = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await apolloClient.query({
        query: GET_USER_SUPPORT_SCOPE,
        variables: { userId },
        fetchPolicy: 'network-only',
      });
      setUserScope(
        userSupportScope(
          data?.getmanageticket || [],
          (data?.linked || []).map((t: { vst_id: string }) => t.vst_id),
        ),
      );
    } catch (err: any) {
      setError(err?.message || "Couldn't load this user's support tickets.");
    }
  }, [apolloClient, userId]);
  useEffect(() => {
    setUserScope(null);
    void loadUserScope();
  }, [loadUserScope]);

  // Replies only reach this page if the qa@ inbox check is working, so keep watching it.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const status = await getVlSupportInboxStatus();
        if (!cancelled) setInboxStatus(status);
      } catch {
        // The backend itself is unreachable; the ticket list shows its own error.
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const [rangeDays, setRangeDays] = useState(90);
  const [insights, setInsights] = useState<VlSupportInsights | null>(null);
  const [insightsType, setInsightsType] = useState<SupportTypeFilter>('all');
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [ownerView, setOwnerView] = useState<'all' | 'open' | 'completed'>('all');
  /** "Time to complete, by event" starts with the latest ten tickets, not the whole history. */
  const [eventRowsShown, setEventRowsShown] = useState(10);
  /** Barcode section: limit to one team's Season Seat Manager account. */
  const [barcodeLeague, setBarcodeLeague] = useState('');
  const [barcodeTeam, setBarcodeTeam] = useState('');
  const [barcodeTeamDraft, setBarcodeTeamDraft] = useState('');
  const [barcodeAccountType, setBarcodeAccountType] = useState('');

  const ownerInitials =
    ownerFilter === 'company' ? null : ownerFilter === 'mine' ? myInitials || '__none__' : ownerFilter;

  const selectTicket = (ticketId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (ticketId) next.set('ticket', ticketId);
    else next.delete('ticket');
    setSearchParams(next, { replace: true });
  };

  const loadTickets = useCallback(async () => {
    if (tab === 'insights') return;
    if (userId && !userScope) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await apolloClient.query({
        query: GET_VL_SUPPORT_TICKETS,
        variables: {
          where: supportTicketsWhere({
            status: tab,
            type: typeFilter,
            ownerInitials,
            // The search box is applied in the browser (below), because it also looks at user names.
            search: '',
            ticketIds: userScope ? (tab === 'sold' ? userScope.soldTicketIds : userScope.ticketIds) : null,
          }),
          scope: userScope ? { vst_id: { _in: userScope.ticketIds } } : {},
          completedSince: completedVisibleSince(),
          // Sorting by event date / time open happens in the browser, so load the whole tab.
          limit: 1000,
          offset: 0,
        },
        fetchPolicy: 'network-only',
      });
      // "Needs reply only" is decided here: it compares two columns of the same ticket.
      const loaded: VlSupportTicketRow[] = data?.vl_support_ticket || [];
      const shown = unreadOnly ? loaded.filter(isWaitingOnUs) : loaded;
      setRows(shown);
      setCounts({
        open: data?.open?.aggregate?.count || 0,
        completed: data?.completed?.aggregate?.count || 0,
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't load support tickets.");
    } finally {
      setLoading(false);
    }
  }, [apolloClient, tab, typeFilter, ownerInitials, unreadOnly, userId, userScope]);

  // Who each listed ticket belongs to: the users behind the listings it was raised on.
  const rowIdsKey = rows.map((r) => r.vst_id).join(',');
  useEffect(() => {
    if (!rowIdsKey) {
      setTicketUsers({});
      return;
    }
    let cancelled = false;
    apolloClient
      .query({ query: GET_VL_SUPPORT_TICKET_USERS, variables: { ids: rowIdsKey.split(',') }, fetchPolicy: 'network-only' })
      .then(async ({ data }) => {
        let users = ticketUsersFromListings(data?.getmanageticket || []);
        // Tickets Victory Live started have no listings: their user is the one linked by hand.
        const links = rows
          .filter((r) => r.vst_user_id)
          .map((r) => ({ ticketId: r.vst_id, userId: r.vst_user_id as string }));
        if (links.length) {
          const named = await apolloClient.query({
            query: GET_USERS_BY_IDS,
            variables: { ids: Array.from(new Set(links.map((l) => l.userId))) },
            fetchPolicy: 'network-only',
          });
          users = addLinkedUsers(users, links, named.data?.user || []);
        }
        if (!cancelled) setTicketUsers(users);
      })
      .catch(() => {
        if (!cancelled) setTicketUsers({});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, rowIdsKey]);

  const loadInsights = useCallback(async () => {
    if (tab !== 'insights') return;
    setInsightsError(null);
    try {
      const to = new Date();
      const from = rangeDays ? new Date(to.getTime() - rangeDays * 86400 * 1000) : new Date('2020-01-01T00:00:00Z');
      setInsights(await getVlSupportInsights({ from: from.toISOString(), to: to.toISOString(), userId: userId ?? null }));
    } catch (err: any) {
      setInsightsError(err?.message || "Couldn't load insights.");
    }
  }, [tab, rangeDays, userId]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    void loadInsights();
  }, [loadInsights]);

  useEffect(() => subscribeVlSupportUpdates(() => void loadTickets()), [loadTickets]);

  useEffect(() => {
    setOffset(0);
  }, [tab, typeFilter, ownerFilter, unreadOnly, search]);

  useEffect(() => {
    setEventRowsShown(10);
  }, [insightsType, rangeDays]);

  const ownerBreakdown = useMemo(() => {
    const byOwner = new Map<string, { open: number; completed: number }>();
    for (const t of insights?.tickets || []) {
      // A ticket with several people in charge counts once for each of them.
      const keys = t.owners?.length ? t.owners : [t.ownerInitials || 'Untagged'];
      for (const key of keys) {
        const entry = byOwner.get(key) || { open: 0, completed: 0 };
        if (t.status === 'open') entry.open += 1;
        else entry.completed += 1;
        byOwner.set(key, entry);
      }
    }
    return byOwner;
  }, [insights]);

  const filteredRows = useMemo(
    () => rows.filter((t) => matchesSupportSearch(t, ticketUsers[t.vst_id] || [], search)),
    [rows, ticketUsers, search],
  );
  const total = filteredRows.length;
  // Freeze the order while a conversation is open, so marking it read can't move it to another page.
  const frozenOrderRef = useRef<string[] | null>(null);
  const freshSortedRows = useMemo(() => sortSupportTickets(filteredRows, sort.key, sort.dir), [filteredRows, sort]);
  // Changing the sort, search, tab or a filter is a deliberate re-order, so it starts a fresh order.
  const frozenKeyRef = useRef('');
  const orderKey = JSON.stringify([sort, search, tab, typeFilter, ownerFilter, unreadOnly]);
  if (frozenKeyRef.current !== orderKey) {
    frozenKeyRef.current = orderKey;
    frozenOrderRef.current = null;
  }
  if (!selectedTicketId) frozenOrderRef.current = null;
  else if (!frozenOrderRef.current) frozenOrderRef.current = freshSortedRows.map((r) => r.vst_id);
  const sortedRows = useMemo(
    () => keepOrderWhileOpen(freshSortedRows, frozenOrderRef.current, selectedTicketId, rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [freshSortedRows, selectedTicketId, rows],
  );
  const pageRows = sortedRows.slice(offset, offset + PAGE_SIZE);

  // Bulk select (Open tab): tick any tickets, then mark them all complete.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const canBulk = tab === 'open';
  // A different list (tab, filter, search) is a different selection.
  useEffect(() => {
    setSelectedIds(new Set());
    setConfirmBulk(false);
  }, [tab, typeFilter, ownerFilter, unreadOnly, search]);
  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pageAllSelected = pageRows.length > 0 && pageRows.every((r) => selectedIds.has(r.vst_id));
  const pageSomeSelected = pageRows.some((r) => selectedIds.has(r.vst_id));
  const togglePage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of pageRows) {
        if (pageAllSelected) next.delete(r.vst_id);
        else next.add(r.vst_id);
      }
      return next;
    });
  const completeSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    setBulkNote(null);
    try {
      const result = await completeVlSupportTickets(ids);
      const done = result.completed + result.alreadyCompleted;
      setBulkNote(
        result.failed.length
          ? { kind: 'err', text: `${done} marked complete. ${result.failed.length} could not be completed, so they are still selected.` }
          : { kind: 'ok', text: `${done} ${done === 1 ? 'ticket' : 'tickets'} marked complete.` },
      );
      setSelectedIds(new Set(result.failed));
      setConfirmBulk(false);
      void loadTickets();
    } catch (err: any) {
      setBulkNote({ kind: 'err', text: err?.message || "Couldn't mark the tickets complete." });
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleSort = (key: SupportSortKey) => {
    setOffset(0);
    setSort((prev) =>
      prev.key === key
        ? prev.dir === 'asc'
          ? { key, dir: 'desc' }
          : { key: 'default', dir: 'desc' }
        : { key, dir: key === 'type' || key === 'owner' || key === 'event' ? 'asc' : 'desc' },
    );
  };

  const SortHeader = ({ label, sortKey }: { label: string; sortKey: SupportSortKey }) => (
    <th>
      <button type="button" className={styles.sortBtn} onClick={() => toggleSort(sortKey)} aria-label={`Sort by ${label}`}>
        {label}
        {sort.key === sortKey ? (
          sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
        ) : (
          <ArrowUpDown size={12} className={styles.sortIdle} />
        )}
      </button>
    </th>
  );

  const insightTickets = useMemo(
    () => (insights?.tickets || []).filter((t) => insightsType === 'all' || t.type === insightsType),
    [insights, insightsType],
  );

  const ownerLabel =
    ownerFilter === 'company'
      ? 'Owner: Company'
      : ownerFilter === 'mine'
        ? `Owner: Mine${myInitials ? ` (${myInitials})` : ''}`
        : `Owner: ${ownerFilter}`;

  return (
    <div className={embedded ? undefined : mt.container}>
      <div className={embedded ? undefined : mt.mainContent}>
        {!embedded && (
        <div className={mt.headerSection}>
          <div className={mt.headerLeft}>
            <p className={mt.subTitle}>Victory Live</p>
            <h1 className={mt.mainTitle}>Support Tickets</h1>
          </div>
          <div className={mt.headerRight}>
            <button
              className={mt.iconButton}
              onClick={() => (tab === 'insights' ? loadInsights() : loadTickets())}
              title="Refresh"
            >
              <RefreshCcw size={16} />
            </button>
          </div>
        </div>
        )}

        {inboxWarning(inboxStatus) && (
          <div className={styles.inboxWarning} role="alert">
            <AlertTriangle size={16} />
            <span>{inboxWarning(inboxStatus)}</span>
          </div>
        )}

        <div className={mt.tabsAndFilterRow}>
          <div className={styles.tabsLine}>
            <div className={styles.tabsBar}>
            {(
              [
                ['open', `Open (${counts.open})`],
                ['completed', `Completed (${counts.completed})`],
                ['insights', 'Insights'],
              ] as Array<[Tab, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`${mt.statusTabBtn} ${tab === value ? mt.activeTab : ''}`}
                onClick={() => setTab(value)}
              >
                {label}
              </button>
            ))}
            </div>
            {tab === 'insights' && (
              <select
                className={styles.rangeSelect}
                value={rangeDays}
                onChange={(e) => setRangeDays(Number(e.target.value))}
                aria-label="Date range"
              >
                {RANGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {tab !== 'insights' && (
            <div className={styles.filterBar}>
              <div className={mt.searchEventWrapper}>
                <SearchIcon size={15} className={mt.searchIconSmall} />
                <input
                  className={mt.searchInputEvent}
                  placeholder="Search subject, event, user, PID, type or owner…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className={mt.filterDropdownWrapper}>
                <button
                  type="button"
                  className={`${mt.filterStatusBtn} ${typeFilter !== 'all' ? mt.filterDropdownItemActive : ''}`}
                  onClick={() => setOpenDropdown((d) => (d === 'type' ? null : 'type'))}
                >
                  <Filter size={13} style={{ marginRight: 4 }} />
                  {TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label}
                </button>
                {openDropdown === 'type' && (
                  <div className={mt.filterDropdown}>
                    {TYPE_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className={`${mt.filterDropdownItem} ${typeFilter === option.value ? mt.filterDropdownItemActive : ''}`}
                        onClick={() => {
                          setTypeFilter(option.value);
                          setOpenDropdown(null);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={mt.filterDropdownWrapper}>
                <button
                  type="button"
                  className={`${mt.filterStatusBtn} ${ownerFilter !== 'company' ? mt.filterDropdownItemActive : ''}`}
                  onClick={() => setOpenDropdown((d) => (d === 'owner' ? null : 'owner'))}
                >
                  <Users size={13} style={{ marginRight: 4 }} />
                  {ownerLabel}
                </button>
                {openDropdown === 'owner' && (
                  <div className={mt.filterDropdown}>
                    {[
                      { value: 'company', label: 'Company (everyone)' },
                      { value: 'mine', label: `My tickets${myInitials ? ` (${myInitials})` : ''}` },
                      ...['Team', ...VL_SUPPORT_OWNERS].map((initials) => ({ value: initials as string, label: initials })),
                    ].map((option) => (
                      <div
                        key={option.value}
                        className={`${mt.filterDropdownItem} ${ownerFilter === option.value ? mt.filterDropdownItemActive : ''}`}
                        onClick={() => {
                          setOwnerFilter(option.value);
                          setOpenDropdown(null);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className={styles.unreadToggle}>
                <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
                <Mail size={13} /> Needs reply only
              </label>

            </div>
          )}
        </div>

        {tab !== 'insights' && (
          <div className={mt.tableContainer}>
            {error ? (
              <div className={styles.error}>{error}</div>
            ) : (
              <>
              {canBulk && (selectedIds.size > 0 || bulkNote) && (
                <div className={styles.bulkBar} role="status">
                  {selectedIds.size > 0 && (
                    <>
                      <strong>{selectedIds.size} selected</strong>
                      {selectedIds.size < total && (
                        <button type="button" className={styles.linkBtn} onClick={() => setSelectedIds(new Set(sortedRows.map((r) => r.vst_id)))}>
                          Select all {total}
                        </button>
                      )}
                      <button type="button" className={styles.linkBtn} onClick={() => setSelectedIds(new Set())}>
                        Clear
                      </button>
                      <span className={styles.spacer} />
                      {confirmBulk ? (
                        <>
                          <span>Mark {selectedIds.size} {selectedIds.size === 1 ? 'ticket' : 'tickets'} complete?</span>
                          <button type="button" className={styles.bulkConfirm} onClick={() => void completeSelected()} disabled={bulkBusy}>
                            {bulkBusy ? 'Working…' : 'Yes, mark complete'}
                          </button>
                          <button type="button" className={styles.linkBtn} onClick={() => setConfirmBulk(false)} disabled={bulkBusy}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button" className={styles.bulkConfirm} onClick={() => setConfirmBulk(true)}>
                          Mark complete
                        </button>
                      )}
                    </>
                  )}
                  {bulkNote && (
                    <span className={bulkNote.kind === 'ok' ? styles.bulkOk : styles.bulkErr}>{bulkNote.text}</span>
                  )}
                </div>
              )}
              <table className={mt.ticketsTable} style={{ width: '100%' }}>
                <thead>
                  <tr>
                    {canBulk && (
                      <th style={{ width: 32 }}>
                        <input
                          type="checkbox"
                          aria-label="Select all tickets on this page"
                          checked={pageAllSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = !pageAllSelected && pageSomeSelected;
                          }}
                          onChange={togglePage}
                        />
                      </th>
                    )}
                    <SortHeader label="Event" sortKey="event" />
                    <SortHeader label="Type" sortKey="type" />
                    <SortHeader label="Owner" sortKey="owner" />
                    <SortHeader label="Opened" sortKey="opened" />
                    <SortHeader label={tab === 'open' ? 'Open for' : 'Time to complete'} sortKey="duration" />
                    <SortHeader label="Last activity" sortKey="lastActivity" />
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((t) => {
                    const selected = selectedTicketId === t.vst_id;
                    const replyNeeded = needsReply(t);
                    return (
                      <React.Fragment key={t.vst_id}>
                        <tr
                          className={[
                            mt.ticketRow,
                            replyNeeded ? styles.rowNeedsReply : '',
                            selected ? styles.rowSelected : '',
                          ].join(' ')}
                          onClick={() => selectTicket(selected ? null : t.vst_id)}
                          style={{ cursor: 'pointer' }}
                          aria-expanded={selected}
                        >
                          {canBulk && (
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                aria-label={`Select ${t.vst_event_name || t.vst_subject}`}
                                checked={selectedIds.has(t.vst_id)}
                                onChange={() => toggleSelected(t.vst_id)}
                              />
                            </td>
                          )}
                          <td>
                            <EventCell name={t.vst_event_name || t.vst_subject} date={t.vst_event_date} unread={t.vst_unread_count} />
                            {isUnlinked(t) && (
                              <div className={styles.unlinkedTag}>
                                From {t.vst_contact_name || t.vst_contact_email || 'Victory Live'} · not linked to a user or event
                              </div>
                            )}
                            {replyNeeded && (
                              <div className={styles.needsReplyTag}>
                                <MessageSquareReply size={12} /> Victory Live replied, needs a reply
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`${styles.typePill} ${styles[`type_${t.vst_type}`]}`}>
                              {SUPPORT_TYPE_LABELS[t.vst_type as VlSupportTicketType]}
                            </span>
                            <MarketplaceLogos names={t.vst_marketplaces} />
                            {t.vst_type === 'barcode_sync' && syncAccountsSummary(t.vst_sync_accounts) && (
                              <div className={styles.muted}>{syncAccountsSummary(t.vst_sync_accounts)}</div>
                            )}
                          </td>
                          <td>
                            {ticketOwners(t).length
                              ? ticketOwners(t).map((o) => (
                                  <span key={o} className={styles.owner} style={{ marginRight: 4 }}>
                                    {o}
                                  </span>
                                ))
                              : '—'}
                          </td>
                          <td className={styles.muted}>
                            {t.vst_type === 'inbound' && <div className={styles.firstEmailTag}>Victory Live emailed</div>}
                            <span title={formatFullDateTime(t.vst_opened_at)}>{formatDateTime(t.vst_opened_at)}</span>
                          </td>
                          <td>
                            {formatDuration(ticketOpenSeconds(t))}
                            {Number(t.vst_reopen_count || 0) > 0 && (
                              <div className={styles.muted}>Reopened {t.vst_reopen_count}×</div>
                            )}
                          </td>
                          <td className={styles.muted}>
                            {formatDateTime(t.vst_last_message_at || t.vst_opened_at)}
                            {t.vst_last_inbound_at && <div>VL replied {formatDateTime(t.vst_last_inbound_at)}</div>}
                          </td>
                          <td>
                            {(ticketUsers[t.vst_id] || []).slice(0, 3).map((u) => (
                              <div key={u.id}>
                                <Link to={`/user/${u.id}`} className={styles.userLink} onClick={(e) => e.stopPropagation()}>
                                  {u.name}
                                </Link>
                              </div>
                            ))}
                            {(ticketUsers[t.vst_id] || []).length > 3 && (
                              <div className={styles.muted}>+{ticketUsers[t.vst_id].length - 3} more</div>
                            )}
                            {!(ticketUsers[t.vst_id] || []).length && '—'}
                          </td>
                        </tr>
                        {selected && (
                          <tr className={styles.threadRow}>
                            <td colSpan={canBulk ? 8 : 7}>
                              <div className={styles.threadPanel}>
                                <VlSupportThread
                                  ticketId={t.vst_id}
                                  onClose={() => selectTicket(null)}
                                  onChanged={() => void loadTickets()}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </>
            )}
            {!error && !loading && filteredRows.length === 0 && (
              <div className={styles.empty}>
                {tab === 'open'
                  ? 'No open support tickets match these filters.'
                  : tab === 'sold'
                    ? 'No support tickets with every listing sold match these filters.'
                    : 'No completed tickets match these filters.'}
              </div>
            )}
            {loading && rows.length === 0 && <div className={styles.empty}>Loading…</div>}
            {total > PAGE_SIZE && (
              <div className={styles.pager}>
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                <button
                  className={styles.pagerBtn}
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  className={styles.pagerBtn}
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'insights' && (
          <>
            {insightsError && <div className={styles.error}>{insightsError}</div>}
            {!insights && !insightsError && <div className={styles.empty}>Loading insights…</div>}
            {insights && (
              <>
                <div className={styles.cards}>
                  {[...insights.byType].sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type)).map((b) => (
                    <div key={b.type} className={styles.card}>
                      <div className={styles.cardTitle}>{SUPPORT_TYPE_LABELS[b.type]}</div>
                      <div className={styles.statGrid}>
                        <div>
                          <div className={styles.statValue}>{formatDuration(b.avgSecondsToComplete)}</div>
                          <div className={styles.statLabel}>Avg time to complete</div>
                        </div>
                        <div>
                          <div className={styles.statValue}>{formatDuration(b.medianSecondsToComplete)}</div>
                          <div className={styles.statLabel}>Median</div>
                        </div>
                        <div>
                          <div className={styles.statValue}>{b.ticketCount}</div>
                          <div className={styles.statLabel}>
                            Tickets ({b.openCount} open, {b.completedCount} done)
                          </div>
                        </div>
                        <div>
                          {b.type === 'inbound' ? (
                            <>
                              <div className={styles.statValue}>—</div>
                              <div className={styles.statLabel}>Started by Victory Live, not tied to listings</div>
                            </>
                          ) : (
                            <>
                              <div className={styles.statValue}>{percent(b.contactRate)}</div>
                              <div className={styles.statLabel}>
                                {b.type === 'need_pid' ? 'Of all listings needing a PID from VL' : 'Of listings'} ({b.listingsContacted} of {b.listingsTotal ?? insights.listingsCount})
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.panels}>
                  <div className={styles.card}>
                    <div className={styles.cardTitle}>Help Syncing Barcodes</div>
                    {(() => {
                      const teams = insights.syncTeams || [];
                      const leagueOfTeam: Record<string, string> = Object.fromEntries(teams.map((t) => [t.team, t.league]));
                      const leagues = Array.from(new Set(teams.map((t) => t.league))).sort();
                      const teamChoices = teams.filter((t) => !barcodeLeague || t.league === barcodeLeague);
                      const pickTeam = (value: string) => {
                        setBarcodeTeamDraft(value);
                        if (!value) return setBarcodeTeam('');
                        const match = teams.find((t) => t.team.toLowerCase() === value.trim().toLowerCase());
                        if (match) {
                          setBarcodeTeam(match.team);
                          // Searching a team from another league switches the league to match.
                          if (barcodeLeague && match.league !== barcodeLeague) setBarcodeLeague(match.league);
                        }
                      };
                      const slices = barcodeSlices(
                        barcodeGroupsFromTickets(
                          insights.tickets,
                          { league: barcodeLeague, team: barcodeTeam, accountType: barcodeAccountType },
                          leagueOfTeam,
                        ),
                      );
                      const filled = slices.filter((x) => x.count > 0);
                      const filtering = Boolean(barcodeLeague || barcodeTeam || barcodeAccountType);
                      return (
                        <>
                          <div className={styles.filterRow}>
                            <select
                              value={barcodeLeague}
                              onChange={(e) => {
                                setBarcodeLeague(e.target.value);
                                const team = teams.find((t) => t.team === barcodeTeam);
                                if (team && e.target.value && team.league !== e.target.value) {
                                  setBarcodeTeam('');
                                  setBarcodeTeamDraft('');
                                }
                              }}
                              aria-label="League"
                            >
                              <option value="">All leagues</option>
                              {leagues.map((l) => (
                                <option key={l} value={l}>
                                  {l}
                                </option>
                              ))}
                            </select>
                            <input
                              list="vl-support-sync-teams"
                              value={barcodeTeamDraft}
                              onChange={(e) => pickTeam(e.target.value)}
                              placeholder={barcodeLeague ? `Search ${barcodeLeague} teams` : 'Search any team'}
                              aria-label="Team"
                            />
                            <datalist id="vl-support-sync-teams">
                              {teamChoices.map((t) => (
                                <option key={t.team} value={t.team} />
                              ))}
                            </datalist>
                            <select value={barcodeAccountType} onChange={(e) => setBarcodeAccountType(e.target.value)} aria-label="Account type">
                              <option value="">All account types</option>
                              {BARCODE_ACCOUNT_SLICES.map((x) => (
                                <option key={x.group} value={x.group}>
                                  {x.label}
                                </option>
                              ))}
                              <option value="Purchase Manager">All [PM] accounts</option>
                            </select>
                            {filtering && (
                              <button
                                type="button"
                                className={styles.clearBtn}
                                onClick={() => {
                                  setBarcodeLeague('');
                                  setBarcodeTeam('');
                                  setBarcodeTeamDraft('');
                                  setBarcodeAccountType('');
                                }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          <div className={styles.pieCentered}>
                            {filled.length ? (
                              <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                  <Pie data={filled} dataKey="count" nameKey="label" innerRadius={55} outerRadius={105} paddingAngle={2}>
                                    {filled.map((x) => (
                                      <Cell key={x.group} fill={x.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value: number, name: string) => [`${value} tickets`, name]} />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className={styles.pieEmpty}>{filtering ? 'No barcode requests match these filters' : 'No barcode requests yet'}</div>
                            )}
                          </div>
                          <div className={styles.legendGrid}>
                            {legendRows(slices).map((x, i) => x ? (
                              <div key={x.group} className={styles.legendRow}>
                                <span className={styles.legendSwatch} style={{ background: x.color }} />
                                <span className={styles.legendName}>
                                  {x.label}
                                  {x.count > 0 && (
                                    <span className={styles.legendDetail}>
                                      {x.openCount} open · avg {formatDuration(x.avgSecondsToComplete)}
                                    </span>
                                  )}
                                </span>
                                <span className={styles.legendCount}>{x.count}</span>
                              </div>
                            ) : (
                              <div key={`gap-${i}`} />
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className={`${styles.card} ${styles.cardCentered}`}>
                    <div className={styles.cardTitle}>Not Populating By Marketplace</div>
                    {(() => {
                      const data = pieCounts(
                        insights.marketplaces.map((m) => ({ key: m.marketplace, count: m.count })),
                        SUPPORT_MARKETPLACES.map((m) => m.name),
                      );
                      const colorFor = (key: string) =>
                        SUPPORT_MARKETPLACES.find((m) => m.name === key)?.color || '#94a3b8';
                      const filled = data.filter((d) => d.count > 0);
                      return (
                        <>
                          <div className={styles.pieCentered}>
                            {filled.length ? (
                              <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                  <Pie data={filled} dataKey="count" nameKey="key" innerRadius={75} outerRadius={145} paddingAngle={2}>
                                    {filled.map((d) => (
                                      <Cell key={d.key} fill={colorFor(d.key)} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value: number, name: string) => [`${value} tickets`, name]} />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className={styles.pieEmpty}>No not-populating tickets yet</div>
                            )}
                          </div>
                          <div className={styles.legendGrid}>
                            {data.map((d) => (
                              <div key={d.key} className={styles.legendRow}>
                                <span className={styles.legendSwatch} style={{ background: colorFor(d.key) }} />
                                {LOGO_ID_BY_NAME[d.key] && <MarketplaceBrandMark id={LOGO_ID_BY_NAME[d.key]} size={20} />}
                                <span className={styles.legendName}>{d.key}</span>
                                <span className={styles.legendCount}>{d.count}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className={`${styles.card} ${styles.ownerCard}`}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitle} style={{ marginBottom: 0 }}>Tickets By Owner</div>
                      <div className={styles.segmented} role="group" aria-label="Ticket status">
                        {(
                          [
                            ['all', 'All tickets'],
                            ['open', 'Open'],
                            ['completed', 'Closed'],
                          ] as Array<['all' | 'open' | 'completed', string]>
                        ).map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            className={`${styles.segBtn} ${ownerView === value ? styles.segBtnOn : ''}`}
                            onClick={() => setOwnerView(value)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const countFor = (c: { open: number; completed: number }) =>
                        ownerView === 'open' ? c.open : ownerView === 'completed' ? c.completed : c.open + c.completed;
                      const data = pieCounts(
                        Array.from(ownerBreakdown.entries()).map(([key, c]) => ({ key, count: countFor(c) })),
                        SUPPORT_OWNER_PIE_ORDER,
                      );
                      const colorFor = (key: string) =>
                        OWNER_COLORS[Math.max(0, [...SUPPORT_OWNER_PIE_ORDER, 'Other'].indexOf(key))];
                      const hasData = data.some((d) => d.count > 0);
                      const detail = (key: string) => {
                        const c =
                          key === 'Other'
                            ? Array.from(ownerBreakdown.entries())
                                .filter(([k]) => !SUPPORT_OWNER_PIE_ORDER.includes(k))
                                .reduce((sum, [, v]) => ({ open: sum.open + v.open, completed: sum.completed + v.completed }), { open: 0, completed: 0 })
                            : ownerBreakdown.get(key) || { open: 0, completed: 0 };
                        return `${c.open} open · ${c.completed} closed`;
                      };
                      const total = data.reduce((sum, d) => sum + d.count, 0);
                      return (
                        <div className={styles.pieWrap} style={{ marginTop: 8 }}>
                          <div className={styles.pieBoxLarge}>
                            {hasData ? (
                              <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                  <Pie data={data.filter((d) => d.count > 0)} dataKey="count" nameKey="key" innerRadius={70} outerRadius={135} paddingAngle={2}>
                                    {data.filter((d) => d.count > 0).map((d) => (
                                      <Cell key={d.key} fill={colorFor(d.key)} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value: number, name: string) => [`${value} tickets`, name]} />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className={styles.pieEmpty}>
                                {ownerView === 'open' ? 'No open tickets' : ownerView === 'completed' ? 'No closed tickets' : 'No tickets in this range'}
                              </div>
                            )}
                          </div>
                          <div className={styles.legend}>
                            {data.map((d) => (
                              <div key={d.key} className={styles.legendRow}>
                                <span className={styles.legendSwatch} style={{ background: colorFor(d.key) }} />
                                <span className={styles.legendName}>
                                  {d.key}
                                  <span className={styles.legendDetail}>{detail(d.key)}</span>
                                </span>
                                <span className={styles.legendCount}>{d.count}</span>
                              </div>
                            ))}
                            <div className={`${styles.legendRow} ${styles.legendTotal}`}>
                              <span className={styles.legendName}>Total</span>
                              <span className={styles.legendCount}>{total}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                </div>

                <div className={mt.tableContainer}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                    <div className={styles.cardTitle} style={{ margin: 0 }}>Time to complete, by event</div>
                    <select
                      className={styles.rangeSelect}
                      value={insightsType}
                      onChange={(e) => setInsightsType(e.target.value as SupportTypeFilter)}
                      aria-label="Ticket type"
                    >
                      {TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <table className={mt.ticketsTable}>
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Type</th>
                        <th>Owner</th>
                        <th>Opened</th>
                        <th>Status</th>
                        <th>Time open</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insightTickets.slice(0, eventRowsShown).map((t) => (
                        <tr
                          key={t.ticketId}
                          className={mt.ticketRow}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setTab(t.status);
                            selectTicket(t.ticketId);
                          }}
                        >
                          <td>
                            <EventCell name={t.eventName || '—'} date={t.eventDate} />
                          </td>
                          <td>
                            <span className={`${styles.typePill} ${styles[`type_${t.type}`]}`}>
                              {SUPPORT_TYPE_LABELS[t.type]}
                            </span>
                          </td>
                          <td>{(t.owners?.length ? t.owners : t.ownerInitials ? [t.ownerInitials] : []).map((o) => (<span key={o} className={styles.owner} style={{ marginRight: 4 }}>{o}</span>))}{!(t.owners?.length || t.ownerInitials) && '—'}</td>
                          <td className={styles.muted}>{formatDateTime(t.openedAt)}</td>
                          <td>{t.status === 'open' ? 'Open' : 'Completed'}</td>
                          <td>
                            {formatDuration(t.secondsOpen)}
                            {t.reopenCount > 0 && <div className={styles.muted}>Reopened {t.reopenCount}×</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {insightTickets.length === 0 && <div className={styles.empty}>No tickets in this range.</div>}
                  {insightTickets.length > eventRowsShown && (
                    <div className={styles.showMore}>
                      <span className={styles.muted}>
                        Showing the latest {eventRowsShown} of {insightTickets.length}
                      </span>
                      <button type="button" className={styles.pagerBtn} onClick={() => setEventRowsShown((n) => n + 10)}>
                        Show 10 more
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
